/**
 * Stimulus axis helpers (materials: audio / passage / image).
 */

import type { QuestionStimulus, RenderConfig } from '../types';

export function normalizeStimulus(value: unknown): QuestionStimulus[] {
  if (!Array.isArray(value)) return [];
  const allowed = new Set<QuestionStimulus>(['audio', 'passage', 'image']);
  const out: QuestionStimulus[] = [];
  for (const item of value) {
    if (typeof item !== 'string') continue;
    const key = item.trim().toLowerCase() as QuestionStimulus;
    if (allowed.has(key) && !out.includes(key)) {
      out.push(key);
    }
  }
  return out;
}

export function questionHasStimulus(
  question: { stimulus?: unknown },
  kind: QuestionStimulus
): boolean {
  return normalizeStimulus(question.stimulus).includes(kind);
}

/**
 * Whether a Skill / section id is a listening skill (audio materials belong here).
 * Used to ignore dirty `stimulus: ['audio']` on grammar/reading inventory.
 */
export function sectionImpliesListeningSkill(section?: string | null): boolean {
  const s = (section ?? '').trim().toLowerCase();
  if (!s) return false;
  return s === 'listening' || s.includes('listen') || s.endsWith('_listening');
}

/**
 * Learner UI: show listening chrome only when stimulus claims audio **and**
 * the question's Skill is listening (or section is unknown — legacy trust).
 * Non-listening Skills never surface "听力材料" for bogus audio tags.
 */
export function questionShowsAudioStimulus(question: {
  stimulus?: unknown;
  section?: string | null;
}): boolean {
  if (!questionHasStimulus(question, 'audio')) return false;
  const section = question.section?.trim();
  if (section && !sectionImpliesListeningSkill(section)) return false;
  return true;
}

/**
 * Default render layout from stimulus. audio wins over passage over image.
 */
export function deriveRenderLayoutFromStimulus(
  stimulus: unknown
): NonNullable<RenderConfig['layout']> {
  const kinds = normalizeStimulus(stimulus);
  if (kinds.includes('audio')) return 'audio-first';
  if (kinds.includes('passage')) return 'passage-first';
  if (kinds.includes('image')) return 'image-first';
  return 'default';
}
